import type { GSplatVector3Input } from '@/plugins/gsplat/effects/effectUtils'
import { writeVector3Array } from '@/plugins/gsplat/effects/effectUtils'
import type { GSplatParticleLifecycleEffectOptions } from '@/plugins/gsplat/effects/GSplatParticleLifecycleEffect'
import { GSplatParticleLifecycleEffect } from '@/plugins/gsplat/effects/GSplatParticleLifecycleEffect'
import {
  GSPLAT_PARTICLE_MOTION_DEFAULTS,
  GSPLAT_PARTICLE_MOTION_LIMITS,
  type GSplatLayeredPrintDirection,
} from '@/plugins/gsplat/effects/particleMotion'
import { createGSplatParticleMotionBounds } from '@/plugins/gsplat/effects/particleMotionBounds'

export interface GSplatLayeredPrintParticleEffectOptions
  extends Omit<GSplatParticleLifecycleEffectOptions, 'scatterScale'> {
  boundsMin?: GSplatVector3Input
  boundsMax?: GSplatVector3Input
  layerCount?: number
  bandWidth?: number
  liftScale?: number
  direction?: GSplatLayeredPrintDirection
}

export const GSPLAT_LAYERED_PRINT_PARTICLE_DEFAULTS = {
  ...GSPLAT_PARTICLE_MOTION_DEFAULTS['layered-print'],
} as const

const layeredPrintMotionFieldGLSL = /* glsl */ `
uniform vec3 uParticleBoundsMin;
uniform vec3 uParticleBoundsMax;
uniform float uParticleLayerCount;
uniform float uParticlePrintBandWidth;
uniform float uParticlePrintLiftScale;
uniform float uParticlePrintDirection;

float gParticleLayeredPrintProgress = 0.0;

float particleLayeredPrintHash(vec3 value) {
  return fract(sin(dot(value, vec3(41.3, 97.1, 173.7))) * 43758.5453123);
}

float particleLayeredPrintLocalProgress(vec3 originalCenter, float scatterProgress) {
  vec3 boundsSize = max(uParticleBoundsMax - uParticleBoundsMin, vec3(0.000001));
  float normalizedHeight = clamp(
    (originalCenter.z - uParticleBoundsMin.z) / boundsSize.z,
    0.0,
    1.0
  );
  normalizedHeight = mix(normalizedHeight, 1.0 - normalizedHeight, uParticlePrintDirection);
  float layerCount = max(uParticleLayerCount, 1.0);
  float layerIndex = min(floor(normalizedHeight * layerCount), layerCount - 1.0);
  float normalizedX = clamp(
    (originalCenter.x - uParticleBoundsMin.x) / boundsSize.x,
    0.0,
    1.0
  );
  float alternatingX = mix(
    normalizedX,
    1.0 - normalizedX,
    step(0.5, mod(layerIndex, 2.0))
  );
  float layeredOrder = clamp((layerIndex + alternatingX) / layerCount, 0.0, 1.0);
  float endpointRange = 1.0 / layerCount;
  float endpointBlend = smoothstep(0.0, endpointRange, normalizedHeight)
    * (1.0 - smoothstep(1.0 - endpointRange, 1.0, normalizedHeight));
  float printOrder = mix(normalizedHeight, layeredOrder, endpointBlend);
  float bandWidth = max(uParticlePrintBandWidth, 0.000001);
  float scanStart = clamp(printOrder - bandWidth * 0.5, 0.0, 1.0 - bandWidth);
  float scanEnd = scanStart + bandWidth;
  return smoothstep(scanStart, scanEnd, clamp(scatterProgress, 0.0, 1.0));
}

vec3 particleLifecycleOffset(vec3 originalCenter, float scatterProgress) {
  float localProgress = particleLayeredPrintLocalProgress(originalCenter, scatterProgress);
  gParticleLayeredPrintProgress = localProgress;
  float effectScale = max(uEffectScale, 0.000001);
  vec3 local = originalCenter / effectScale;
  vec3 randomValue = vec3(
    particleLayeredPrintHash(local + vec3(uParticleSeed + 11.0, 3.0, 17.0)),
    particleLayeredPrintHash(local.yzx + vec3(5.0, uParticleSeed + 23.0, 13.0)),
    particleLayeredPrintHash(local.zxy + vec3(19.0, 7.0, uParticleSeed + 29.0))
  ) * 2.0 - 1.0;
  float randomLength = max(length(randomValue), 0.000001);
  vec3 cloudDirection = randomValue / randomLength;
  float directionSign = mix(1.0, -1.0, uParticlePrintDirection);
  vec3 liftOffset = vec3(0.0, 0.0, directionSign)
    * uParticlePrintLiftScale
    * effectScale;
  vec3 cloudOffset = cloudDirection * uParticlePrintLiftScale * effectScale * 0.65;
  return (liftOffset + cloudOffset) * localProgress;
}
`

const layeredPrintColorFieldGLSL = /* glsl */ `
void particleLifecycleModifyColor(vec3 center, inout vec4 color) {
  float scanBand = 1.0 - abs(gParticleLayeredPrintProgress - 0.5) * 2.0;
  scanBand = smoothstep(0.15, 1.0, clamp(scanBand, 0.0, 1.0));
  vec3 scanColor = vec3(0.0, 1.0, 0.8);
  color.rgb = mix(color.rgb, scanColor * 1.5, scanBand * 0.9 * uParticleMix);
}
`

const layeredPrintMotionFieldWGSL = /* wgsl */ `
var<private> gParticleLayeredPrintProgress: f32 = 0.0;

fn particleLayeredPrintHash(value: vec3f) -> f32 {
  return fract(sin(dot(value, vec3f(41.3, 97.1, 173.7))) * 43758.5453123);
}

fn particleLayeredPrintLocalProgress(originalCenter: vec3f, scatterProgress: f32) -> f32 {
  let boundsSize = max(uniform.uParticleBoundsMax - uniform.uParticleBoundsMin, vec3f(0.000001));
  var normalizedHeight = clamp(
    (originalCenter.z - uniform.uParticleBoundsMin.z) / boundsSize.z,
    0.0,
    1.0
  );
  normalizedHeight = mix(
    normalizedHeight,
    1.0 - normalizedHeight,
    uniform.uParticlePrintDirection
  );
  let layerCount = max(uniform.uParticleLayerCount, 1.0);
  let layerIndex = min(floor(normalizedHeight * layerCount), layerCount - 1.0);
  let normalizedX = clamp(
    (originalCenter.x - uniform.uParticleBoundsMin.x) / boundsSize.x,
    0.0,
    1.0
  );
  let alternatingX = mix(normalizedX, 1.0 - normalizedX, step(0.5, layerIndex % 2.0));
  let layeredOrder = clamp((layerIndex + alternatingX) / layerCount, 0.0, 1.0);
  let endpointRange = 1.0 / layerCount;
  let endpointBlend = smoothstep(0.0, endpointRange, normalizedHeight)
    * (1.0 - smoothstep(1.0 - endpointRange, 1.0, normalizedHeight));
  let printOrder = mix(normalizedHeight, layeredOrder, endpointBlend);
  let bandWidth = max(uniform.uParticlePrintBandWidth, 0.000001);
  let scanStart = clamp(printOrder - bandWidth * 0.5, 0.0, 1.0 - bandWidth);
  let scanEnd = scanStart + bandWidth;
  return smoothstep(scanStart, scanEnd, clamp(scatterProgress, 0.0, 1.0));
}

fn particleLifecycleOffset(originalCenter: vec3f, scatterProgress: f32) -> vec3f {
  let localProgress = particleLayeredPrintLocalProgress(originalCenter, scatterProgress);
  gParticleLayeredPrintProgress = localProgress;
  let effectScale = max(uniform.uEffectScale, 0.000001);
  let local = originalCenter / effectScale;
  let randomValue = vec3f(
    particleLayeredPrintHash(local + vec3f(uniform.uParticleSeed + 11.0, 3.0, 17.0)),
    particleLayeredPrintHash(local.yzx + vec3f(5.0, uniform.uParticleSeed + 23.0, 13.0)),
    particleLayeredPrintHash(local.zxy + vec3f(19.0, 7.0, uniform.uParticleSeed + 29.0))
  ) * 2.0 - 1.0;
  let randomLength = max(length(randomValue), 0.000001);
  let cloudDirection = randomValue / randomLength;
  let directionSign = mix(1.0, -1.0, uniform.uParticlePrintDirection);
  let liftOffset = vec3f(0.0, 0.0, directionSign)
    * uniform.uParticlePrintLiftScale
    * effectScale;
  let cloudOffset = cloudDirection * uniform.uParticlePrintLiftScale * effectScale * 0.65;
  return (liftOffset + cloudOffset) * localProgress;
}
`

const layeredPrintColorFieldWGSL = /* wgsl */ `
fn particleLifecycleModifyColor(center: vec3f, color: ptr<function, vec4f>) {
  var scanBand = 1.0 - abs(gParticleLayeredPrintProgress - 0.5) * 2.0;
  scanBand = smoothstep(0.15, 1.0, clamp(scanBand, 0.0, 1.0));
  let scanColor = vec3f(0.0, 1.0, 0.8);
  *color = vec4f(
    mix((*color).rgb, scanColor * 1.5, vec3f(scanBand * 0.9 * uniform.uParticleMix)),
    (*color).a
  );
}
`

/** 沿引擎 Z 高度轴逐层扫描并原路重组的粒子效果。 */
export class GSplatLayeredPrintParticleEffect extends GSplatParticleLifecycleEffect {
  readonly layerCount: number
  readonly bandWidth: number
  readonly liftScale: number
  readonly direction: GSplatLayeredPrintDirection

  private readonly _boundsMin: ReturnType<typeof createGSplatParticleMotionBounds>['min']
  private readonly _boundsMax: ReturnType<typeof createGSplatParticleMotionBounds>['max']
  private readonly _boundsMinArray = [0, 0, 0]
  private readonly _boundsMaxArray = [0, 0, 0]

  constructor(options: GSplatLayeredPrintParticleEffectOptions = {}) {
    super({
      ...options,
      id: options.id ?? 'particle-layered-print',
    })

    const defaults = GSPLAT_LAYERED_PRINT_PARTICLE_DEFAULTS
    const limits = GSPLAT_PARTICLE_MOTION_LIMITS['layered-print']
    const bounds = createGSplatParticleMotionBounds(options, this.effectScale)
    this._boundsMin = bounds.min
    this._boundsMax = bounds.max
    this.layerCount = Math.round(
      normalizeRange(options.layerCount, limits.layerCount, defaults.layerCount)
    )
    this.bandWidth = normalizeRange(options.bandWidth, limits.bandWidth, defaults.bandWidth)
    this.liftScale = normalizeRange(options.liftScale, limits.liftScale, defaults.liftScale)
    this.direction = options.direction === 'top-down' ? 'top-down' : defaults.direction
  }

  protected override getParticleMotionFieldGLSL(): string {
    return layeredPrintMotionFieldGLSL
  }

  protected override getParticleColorFieldGLSL(): string {
    return layeredPrintColorFieldGLSL
  }

  protected override getParticleMotionFieldWGSL(): string {
    return layeredPrintMotionFieldWGSL
  }

  protected override getParticleColorFieldWGSL(): string {
    return layeredPrintColorFieldWGSL
  }

  protected override updateEffect(effectTime: number): void {
    super.updateEffect(effectTime)
    this.setUniform('uParticleBoundsMin', writeVector3Array(this._boundsMin, this._boundsMinArray))
    this.setUniform('uParticleBoundsMax', writeVector3Array(this._boundsMax, this._boundsMaxArray))
    this.setUniform('uParticleLayerCount', this.layerCount)
    this.setUniform('uParticlePrintBandWidth', this.bandWidth)
    this.setUniform('uParticlePrintLiftScale', this.liftScale)
    this.setUniform('uParticlePrintDirection', this.direction === 'top-down' ? 1 : 0)
  }
}

function normalizeRange(
  value: number | undefined,
  limits: { min: number; max: number },
  fallback: number
): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback
  }
  return Math.min(limits.max, Math.max(limits.min, value))
}
